import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TopNav } from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { AuraMark } from "@/components/AuraMark";
import { MuseLink, linkifyMuse } from "@/components/MuseLink";
import {
  Mic, FileText, Dna, Waves, Boxes, Radio, ShieldCheck, Cpu, ArrowRight, Zap, CircleDollarSign, Monitor,
} from "lucide-react";

const fade = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } };

const EqBars = () => (
  <div className="flex items-end gap-[3px] h-16" aria-hidden="true">
    {Array.from({ length: 28 }).map((_, i) => (
      <span key={i} className="eq-bar w-1 rounded-full"
        style={{ height: `${20 + (i % 5) * 15}%`, animationDelay: `${i * 0.06}s`,
          background: i % 3 === 0 ? "#C8A15C" : i % 3 === 1 ? "#E7C892" : "#8A6A28" }} />
    ))}
  </div>
);

const features = [
  { icon: FileText, t: "Aura Input Studio", d: "Type, paste, drop a TXT, feed Muse Beats or speak your brief. Every input becomes structured intent.", c: "text-cyan" },
  { icon: Dna, t: "Production DNA", d: "AuraSession's proprietary engine breaks your reference down into tempo, key, groove, sound design & vocal space, with evidence.", c: "text-violet" },
  { icon: Waves, t: "Composition Engine", d: "Drums, 808s, harmony, melody and full arrangement, generated as real MIDI, not vibes.", c: "text-amber" },
  { icon: Boxes, t: "Universal DAW Package", d: "One export imports into every DAW: MIDI + manifest + a REAPER ReaScript that builds the session.", c: "text-cyan" },
  { icon: Radio, t: "Aura Vision Cockpit", d: "Watch every build step stream live. Nothing is faked, each event is a real operation.", c: "text-violet" },
  { icon: CircleDollarSign, t: "Honest Credits", d: "Metered per operation with an immutable ledger. Failed jobs auto-refund. You always see the quote first.", c: "text-amber" },
];

const tiers = [
  { name: "REAPER", tier: "Companion", note: "ReaScript auto-builds the session" },
  { name: "Ableton Live", tier: "Companion", note: "MIDI clips + arrangement guide" },
  { name: "FL Studio", tier: "Package", note: "Import MIDI + manifest" },
  { name: "Logic Pro", tier: "Package", note: "Import MIDI + manifest" },
  { name: "Cubase", tier: "Package", note: "Import MIDI + manifest" },
  { name: "Bitwig", tier: "Package", note: "Import MIDI + manifest" },
];

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen relative">
      <TopNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src="https://images.pexels.com/photos/7095424/pexels-photo-7095424.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1600"
            alt="" className="w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(8,8,10,0.94) 0%, rgba(8,8,10,0.82) 42%, rgba(8,8,10,0.35) 100%)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(8,8,10,0) 55%, #08080A 100%)" }} />
        </div>
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.12 } } }} className="max-w-3xl">
            <motion.div variants={fade} className="inline-flex flex-wrap items-center gap-2 mb-7">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/5">
                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                <span className="label-mono text-cyan">From reference to record, operationalised</span>
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-400/30 bg-gold-400/5">
                <Monitor className="w-3.5 h-3.5 text-gold-400" />
                <span className="label-mono text-gold-400">Desktop-first · best on Mac &amp; desktop</span>
              </span>
            </motion.div>
            <motion.h1 variants={fade} className="font-display font-extrabold tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[0.95] text-slate-50">
              From a whisper of an idea<br />to a <span className="text-cyan">session-ready</span> track.
            </motion.h1>
            <motion.p variants={fade} className="mt-7 text-lg text-slate-300 max-w-xl leading-relaxed">
              AuraSession turns text, <MuseLink>Muse Beats</MuseLink>, lyrics or your voice into a full Production DNA, a real
              MIDI arrangement, and a Universal DAW package that drops straight into REAPER, Ableton and beyond.
            </motion.p>
            <motion.div variants={fade} className="mt-9 flex flex-wrap items-center gap-4">
              <Button data-testid="hero-start-btn" onClick={() => nav("/login")}
                className="rounded-full h-12 px-7 bg-cyan-400 text-black hover:bg-cyan-300 font-semibold text-base group">
                Start a session <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button data-testid="hero-pricing-btn" variant="outline" onClick={() => nav("/pricing")}
                className="rounded-full h-12 px-7 border-white/15 bg-white/5 hover:bg-white/10 text-slate-100 font-semibold text-base">
                See plans & credits
              </Button>
            </motion.div>
            <motion.div variants={fade} className="mt-12"><EqBars /></motion.div>
          </motion.div>
        </div>
      </section>

      {/* No free service band */}
      <section className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <Zap className="w-5 h-5 text-amber" />
            <p className="text-slate-300 text-sm sm:text-base">
              <span className="text-slate-50 font-semibold">A serious tool for serious producers.</span> AI production is metered and paid, no free generations, ever. You always see the credit quote before anything runs.
            </p>
          </div>
          <Button data-testid="band-pricing-btn" variant="ghost" onClick={() => nav("/pricing")} className="text-cyan hover:text-cyan-300 shrink-0">
            View pricing <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>

      {/* Features bento */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24">
        <div className="max-w-2xl mb-14">
          <p className="label-mono text-violet mb-3">The pipeline</p>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-slate-50">
            Everything from brief to bounce, in one signal chain.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <motion.div key={f.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              data-testid={`feature-card-${i}`}
              className="card-surface rounded-2xl p-7 hover:border-white/25 transition-colors group">
              <div className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <f.icon className={`w-5 h-5 ${f.c}`} />
              </div>
              <h3 className="font-display font-semibold text-xl text-slate-50 mb-2">{f.t}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{linkifyMuse(f.d)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* DAW capability */}
      <section className="border-t border-white/10 bg-white/[0.015]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24">
          <div className="flex flex-col lg:flex-row gap-12">
            <div className="lg:w-1/3">
              <p className="label-mono text-cyan mb-3">All-DAW honesty</p>
              <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-slate-50 mb-5">
                We never lie about what your DAW can do.
              </h2>
              <p className="text-slate-400 leading-relaxed">
                Every target is tagged <span className="text-cyan font-medium">Native</span>, <span className="text-violet font-medium">Companion</span>, <span className="text-amber font-medium">Package</span> or Unsupported, so you always know exactly how a build lands.
              </p>
            </div>
            <div className="lg:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {tiers.map((t) => (
                <div key={t.name} data-testid={`daw-tier-${t.name}`} className="tracing-border rounded-xl p-5 flex items-center justify-between">
                  <div>
                    <p className="font-display font-semibold text-slate-50">{t.name}</p>
                    <p className="text-slate-500 text-xs mt-0.5">{t.note}</p>
                  </div>
                  <span className={`label-mono px-2.5 py-1 rounded-full ${t.tier === "Companion" ? "text-violet bg-violet-500/10" : "text-amber bg-amber-500/10"}`}>
                    {t.tier}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24">
        <div className="tracing-border rounded-3xl p-10 sm:p-16 text-center relative overflow-hidden">
          <AuraMark className="w-14 h-14 mx-auto mb-6 floaty" />
          <h2 className="font-display font-extrabold tracking-tighter text-3xl sm:text-5xl text-slate-50 max-w-2xl mx-auto">
            Your next hit is one brief away.
          </h2>
          <p className="text-slate-400 mt-5 max-w-lg mx-auto">Sign in with Google, subscribe, and start turning your references into your own sound.</p>
          <Button data-testid="cta-start-btn" onClick={() => nav("/login")}
            className="mt-8 rounded-full h-12 px-8 bg-cyan-400 text-black hover:bg-cyan-300 font-semibold text-base">
            Enter the studio
          </Button>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2"><AuraMark className="w-6 h-6" /><span className="text-slate-500 text-sm">© 2026 AuraSession · Powered by <span className="text-gold">SoundForgeArena</span></span></div>
          <div className="flex items-center gap-4 text-slate-500 text-sm flex-wrap justify-center">
            <a href="/policies?tab=privacy" data-testid="footer-privacy" className="hover:text-slate-200 transition-colors">Privacy</a>
            <a href="/policies?tab=terms" data-testid="footer-terms" className="hover:text-slate-200 transition-colors">Terms</a>
            <a href="/policies?tab=refund" data-testid="footer-refund" className="hover:text-slate-200 transition-colors">Refunds</a>
            <a href="/policies?tab=ai-data" data-testid="footer-ai" className="hover:text-slate-200 transition-colors">AI &amp; Voice Data</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
